import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Modal, Alert, Spin } from "antd";
import { 
  VideoCameraOutlined, 
  CloseOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import useBodyScrollLock from "../../../../../hooks/useBodyScrollLock";
import "../shared/sharedModalStyles.scss";
import "./ZoomMeetingModal.scss";

/**
 * Zoom Meeting Modal Component
 * 
 * Modern, beautiful, and fully responsive modal for scheduling Zoom meetings.
 * 
 * @component
 */
const ZoomMeetingModal = ({
  open,
  onCancel,
  schedulerUrl = null,
  slotError = null,
  lead = null,
}) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  const userEmail = useMemo(() => {
    return lead?.Email || lead?.email || "";
  }, [lead]);

  const iframeSrc = useMemo(() => {
    if (!schedulerUrl) return "";
    try {
      const url = new URL(schedulerUrl);
      url.searchParams.set("embed", "true");
      if (userEmail) url.searchParams.set("email", userEmail);
      return url.toString();
    } catch {
      return schedulerUrl;
    }
  }, [schedulerUrl, userEmail]);

  useEffect(() => {
    if (schedulerUrl) setIframeLoading(true);
  }, [schedulerUrl]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setIframeLoading(true);
      onCancel?.();
    }, 300);
  }, [onCancel]);

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleClose}
      footer={null}
      width="100%"
      className={`rate-review-zoom-modal ${isVisible ? "visible" : ""}`}
      destroyOnClose
      centered={true}
      getContainer={() => document.body}
      zIndex={1500}
      closeIcon={null}
      maskClosable={true}
      keyboard={true}
      styles={{
        body: { 
          padding: 0, 
          height: "auto",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        content: { 
          borderRadius: "16px",
          width: "100%",
          height: "auto",
          padding: 0,
          margin: "0 auto",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div className="zoom-modal-wrapper">
        {/* Modern Header */}
        <div className="zoom-modal-header-modern">
          <div className="zoom-header-inner">
            <div className="zoom-header-main">
              <div className="zoom-icon-badge">
                <VideoCameraOutlined />
              </div>
              <div className="zoom-header-text-group">
                <h2 className="zoom-header-title">Schedule Zoom Meeting</h2>
                <p className="zoom-header-subtitle">Quick & Easy Scheduling</p>
              </div>
            </div>
            <button 
              className="zoom-close-button"
              onClick={handleClose}
              aria-label="Close modal"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="zoom-modal-content-modern">
          {/* Error Alert */}
          {slotError && (
            <div className="zoom-alert-section">
              <Alert
                message={slotError}
                type="error"
                className="zoom-alert"
                showIcon
                icon={<InfoCircleOutlined />}
              />
            </div>
          )}

          {/* Zoom Scheduler Container */}
          <div className="zoom-scheduler-container">
            {!schedulerUrl ? (
              <div className="zoom-empty-view">
                <div className="zoom-empty-icon-circle">
                  <VideoCameraOutlined className="zoom-empty-icon" />
                </div>
                <h3 className="zoom-empty-title">
                  Zoom Scheduler Not Configured
                </h3>
                <p className="zoom-empty-message">
                  Please contact support to set up the Zoom scheduler.
                </p>
              </div>
            ) : (
              <div className="zoom-iframe-container">
                {iframeLoading && (
                  <div className="zoom-loading-overlay">
                    <Spin size="large" />
                    <p className="zoom-loading-text">Loading Zoom Scheduler...</p>
                  </div>
                )}
                <iframe
                  title="Zoom Scheduler"
                  src={iframeSrc}
                  frameBorder="0"
                  className={`zoom-iframe ${iframeLoading ? "loading" : "loaded"}`}
                  allow="microphone; camera; fullscreen"
                  allowFullScreen
                  onLoad={handleIframeLoad}
                  aria-label="Zoom meeting scheduler"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ZoomMeetingModal;
